/**
 * ShopSalesDaily Component
 * --
 * 작성자 - Owen
 * 작성일 - 2025 07 15 화요일
 * 마지막 수정일 - 2025 07 15 화요일
 */
import React from 'react';
import dayjs from 'dayjs';
import {
  BarChart,
  Bar,
  Rectangle,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { Col, Content, Row, Title } from 'components';
import { Divider, Table, Flex } from 'antd';
import { stringToMoneyFormat } from 'utils';

const ShopSalesDaily = (props) => {
  /**
   * SECTION - Init
   */
  const COLORS = [
    '#8884d8',
    '#82CA9D',
    '#FFC658',
    '#1677FF',
    '#FF8042',
    '#8DD1E1',
    '#83A6ED',
  ];
  const dateFormat = 'YYYY-MM-DD';
  /**!SECTION - Init */

  /**
   * SECTION - Route
   */

  /**!SECTION - Route */

  /**
   * SECTION - Props
   */
  const {
    shops = [],
    period = 7,
    orders = [],
    delivery = [],
    subscription = [],
    rental = [],
  } = props;
  /**!SECTION - Props */

  /**
   * SECTION - Route
   */

  /**!SECTION - Route */

  /**
   * SECTION - Variable
   */

  /**!SECTION - Variable */

  /**
   * SECTION - Query Data
   */

  /**!SECTION - Query Data */

  /**
   * SECTION - Mutate
   */

  /**!SECTION - Mutate */

  /**
   * SECTION - State
   */

  /**!SECTION - State */

  /**
   * SECTION - Function
   */

  /**!SECTION - Function */

  /**
   * SECTION - Hooks
   */
  const PeriodDate = React.useMemo(() => {
    return new Array(period)
      .fill(null)
      .map((_, index) => dayjs().subtract(index, 'day').format(dateFormat));
  }, [period]);

  // 테이블 컬럼 설정
  const TableColumns = React.useMemo(() => {
    return [
      {
        title: '날짜',
        dataIndex: 'date',
        key: 'date',
        render: (item) => dayjs(item).locale('ko').format('YYYY-MM-DD (dd)'),
      },
      {
        title: '총매출',
        dataIndex: 'totalAmount',
        key: 'totalAmount',
        render: (item) => stringToMoneyFormat(item),
      },
      {
        title: '순매출',
        dataIndex: 'totalNetAmount',
        key: 'totalNetAmount',
        render: (item) => stringToMoneyFormat(item),
      },
      {
        title: '주문건수',
        dataIndex: 'totalCount',
        key: 'totalCount',
        render: (item) => stringToMoneyFormat(item),
      },
      {
        title: '할인금액',
        dataIndex: 'totalDiscountAmount',
        key: 'totalDiscountAmount',
        render: (item) => stringToMoneyFormat(item),
      },
      {
        title: '환불금액',
        dataIndex: 'totalRefundAmount',
        key: 'totalRefundAmount',
        render: (item) => stringToMoneyFormat(item),
      },
      {
        title: '환불건수',
        dataIndex: 'totalRefundCount',
        key: 'totalRefundCount',
        render: (item) => stringToMoneyFormat(item),
      },
    ];
  }, []);

  // 테이블 데이터 설정
  const TableData = React.useMemo(() => {
    if (!PeriodDate.length) return [];

    const salesMap = new Map();

    // 날짜별 Map 데이터 생성
    PeriodDate.forEach((item) => {
      salesMap.set(item, []);
    });

    // 날짜별 ShopOrder 데이터 Push
    orders.forEach((item) => {
      const { payment_completion_date } = item;

      const date = dayjs.unix(payment_completion_date).format(dateFormat);

      if (salesMap.has(date)) {
        salesMap.get(date).push(item);
      }
    });

    // 날짜별 Parcel, Rental 데이터 Push
    const newList = delivery.concat(subscription).concat(rental);
    newList.forEach((item) => {
      const { type, payment_date } = item;

      let paymentDate = null;
      if (type === 'DELIVERY' || type === 'STORE_PICKUP') {
        paymentDate = dayjs.unix(payment_date).format(dateFormat);
      } else if (type === 'SUBSCRIPTION') {
        const { payment_date: regularPaymentDate } = item.regular_payment_info;

        paymentDate = dayjs.unix(regularPaymentDate).format(dateFormat);
      } else {
        const { payment_date: rentalPaymentDate } = item.rental_payment_info;

        paymentDate = dayjs.unix(rentalPaymentDate).format(dateFormat);
      }

      if (salesMap.has(paymentDate)) {
        salesMap.get(paymentDate).push(item);
      }
    });

    const initial = {
      totalDeliveryAmount: 0,
      totalPointAmount: 0,
      totalRefundAmount: 0,
      totalDiscountAmount: 0,
      totalPaymentAmount: 0,
      totalCount: 0,
      totalRefundCount: 0,
    };

    const summary = Array.from(salesMap.entries()).reduce(
      (sum, [key, value]) => {
        // 날짜별 합산
        const valueSummary = value.reduce(
          (acc, cur) => {
            const { parcel_service_id, rental_id, type, refund } = cur;

            // shop_order 합산
            if (!parcel_service_id && !rental_id) {
              acc.totalDeliveryAmount += cur.final_delivery_price;
              acc.totalPointAmount += Math.abs(cur.use_point);
            }
            // parcel, rental 합산
            else {
              if (refund) {
                acc.totalRefundAmount += refund.refund_amount;
                acc.totalRefundCount += 1;
              }

              acc.totalCount += 1;
              if (type === 'DELIVERY' || type === 'STORE_PICKUP') {
                acc.totalPaymentAmount += cur.payment_price;
                acc.totalDiscountAmount += Math.abs(cur.discount_price ?? 0);
              } else if (type === 'SUBSCRIPTION') {
                acc.totalPaymentAmount += cur.regular_payment_info.price;
                acc.totalDiscountAmount +=
                  cur.regular_payment_info.regular_payment_id === 1
                    ? Math.abs(cur?.discount_price) ?? 0
                    : Math.abs(cur?.regular_payment_info?.discount_price ?? 0);
              } else {
                acc.totalPaymentAmount += cur.rental_payment_info.price;
                acc.totalDiscountAmount +=
                  cur.rental_payment_info.rental_payment_id === 1
                    ? Math.abs(cur?.discount_price ?? 0)
                    : Math.abs(cur?.rental_payment_info?.discount_price ?? 0);
              }
            }

            return acc;
          },
          { ...initial }
        );

        sum[key] = valueSummary;

        return sum;
      },
      {}
    );

    // 테이블 데이터 정제
    const newData = Object.entries(summary).map(([key, value]) => {
      return {
        date: key,
        ...value,
        totalAmount:
          value.totalPaymentAmount +
          value.totalDiscountAmount +
          value.totalPointAmount +
          value.totalDeliveryAmount,
        totalNetAmount:
          value.totalPaymentAmount -
          value.totalRefundAmount +
          value.totalDeliveryAmount,
        totalDiscountAmount: value.totalDiscountAmount + value.totalPointAmount,
      };
    });

    return newData;
  }, [PeriodDate, orders, delivery, subscription, rental]);
  /**!SECTION - Hooks */

  /**
   * SECTION - Render
   */
  return (
    <Content maxWidth={'100%'} padding={20}>
      <Row gutter={[16, 16]}>
        <Col x={24}>
          <Title size={5}>일별 매출</Title>
          <Divider style={{ margin: '12px 0px' }} />
        </Col>

        {/* 차트 */}
        <Col x={10}>
          <Content
            maxWidth={'100%'}
            padding={0}
            style={{ overflowY: 'scroll' }}
          >
            <ResponsiveContainer width={'100%'} height={350}>
              <BarChart
                width={'100%'}
                height={350}
                data={TableData}
                margin={{
                  top: 20,
                  right: 30,
                  left: 20,
                  bottom: 5,
                }}
                barSize={20}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar
                  dataKey={'totalAmount'}
                  fill={'#8884d8'}
                  minPointSize={5}
                  name={'총매출'}
                />
                <Bar
                  dataKey={'totalNetAmount'}
                  fill={'#82CA9D'}
                  minPointSize={5}
                  name={'순매출'}
                />
              </BarChart>
            </ResponsiveContainer>
          </Content>
        </Col>

        {/* 테이블 */}
        <Col x={14}>
          <Table
            size="small"
            bordered
            columns={TableColumns}
            dataSource={TableData}
            scroll={{ x: 150 * TableColumns.length }}
            pagination={false}
            summary={(pageData) => {
              const summaryData = pageData.reduce(
                (acc, cur) => {
                  Object.keys(acc).forEach(
                    (key) => (acc[key] += cur[key] ?? 0)
                  );

                  return acc;
                },
                {
                  totalAmount: 0,
                  totalNetAmount: 0,
                  totalCount: 0,
                  totalDiscountAmount: 0,
                  totalRefundAmount: 0,
                  totalRefundCount: 0,
                }
              );

              return (
                <Table.Summary fixed>
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0}>
                      <span style={{ fontWeight: 700 }}>합계</span>
                    </Table.Summary.Cell>
                    {Object.keys(summaryData).map((key, index) => (
                      <Table.Summary.Cell
                        key={`summary-${index}-${key}`}
                        index={index + 1}
                      >
                        <Flex vertical>
                          {stringToMoneyFormat(summaryData[key])}
                        </Flex>
                      </Table.Summary.Cell>
                    ))}
                  </Table.Summary.Row>
                </Table.Summary>
              );
            }}
          />
        </Col>
      </Row>
    </Content>
  );
  /**!SECTION - Render */
};

export default ShopSalesDaily;
